import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { companyInfo as defaultCompanyInfo } from '../data/mock';
import { Button } from './ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [content, setContent] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/content`);
        setContent(response.data);
      } catch (error) {
        console.error('Error fetching content:', error);
      }
    };
    fetchContent();
  }, []);

  const logoUrl = content?.logo_url || defaultCompanyInfo.logo;
  const phone = content?.phone || defaultCompanyInfo.phone;

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Contact', path: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-navy/95 backdrop-blur-md border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-32">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src={logoUrl} 
              alt="United ELD LLC" 
              className="h-28 md:h-32 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors duration-200 ${
                  isActive(link.path)
                    ? 'text-red-500'
                    : 'text-white/80 hover:text-white'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-4">
            <a 
              href={`tel:${phone}`}
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <Phone className="h-4 w-4" />
              <span className="text-sm font-medium">{phone}</span>
            </a>
            <Link to="/contact">
              <Button className="bg-red-600 hover:bg-red-700 text-white font-semibold px-6">
                Get Demo
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-navy border-t border-white/10">
          <div className="px-4 py-4 space-y-3">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMenuOpen(false)}
                className={`block py-2 text-base font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-red-500'
                    : 'text-white/80 hover:text-white'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <a 
              href={`tel:${phone}`}
              className="flex items-center gap-2 text-white/80 py-2"
            >
              <Phone className="h-4 w-4" />
              <span>{phone}</span>
            </a>
            <Link to="/contact" onClick={() => setIsMenuOpen(false)}>
              <Button className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold mt-2">
                Get Demo
              </Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
